
import { Home, User, Target, Users, Map, Stamp } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { AuthModal } from "@/components/auth/AuthModal";
import { MobileNavItem } from "./MobileNavItem";
import { MobileNavMoreMenu } from "./MobileNavMoreMenu";
import { MobileUserHub } from "./MobileUserHub";
import { useRole } from "@/hooks/useRole";
import { logger } from "@/utils/logger";

export function MobileNavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { role } = useRole();
  const { toast } = useToast();
  const [visible, setVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  // Handle scrolling behavior - hide nav when scrolling down, show when scrolling up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setVisible(false);
      } else {
        setVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };
    
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [lastScrollY]);

  // Determine if we need compact labels based on screen size
  const useCompactLabels = screenWidth < 360;

  // Handle profile actions
  const handleSignIn = () => {
    setIsAuthModalOpen(true);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Signed out successfully",
        description: "You have been signed out of your account"
      });
    } catch (error) {
      logger.error("Sign out error:", error);
      toast({
        title: "Error signing out",
        description: "There was a problem signing out",
        variant: "destructive"
      });
    }
  };

  return (
    <>
      <nav
        aria-label="Main navigation"
        className={`fixed bottom-0 left-0 right-0 z-50 md:hidden pointer-events-none transition-transform duration-300 ${
          visible ? 'translate-y-0' : 'translate-y-[calc(100%+1rem)]'
        }`}
        style={{
          paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))',
          paddingLeft: 'calc(0.75rem + env(safe-area-inset-left, 0px))',
          paddingRight: 'calc(0.75rem + env(safe-area-inset-right, 0px))',
        }}
      >
        <div className="gd-dock pointer-events-auto mx-auto max-w-md rounded-[28px] px-1.5 py-1.5">
          <div className="flex justify-around items-center h-full">
            {/* 4-Pillar Navigation */}

            {/* Home */}
            <MobileNavItem
              icon={<Home className="h-5 w-5" />}
              label="Home"
              onClick={() => navigate('/dashboard')}
              useCompactLabel={useCompactLabels}
              isActive={location.pathname === '/' || location.pathname === '/dashboard'}
            />

            {/* Pillar 1: My Goals */}
            <MobileNavItem
              icon={<Target className="h-5 w-5" />}
              label="Goals"
              onClick={() => navigate('/goals')}
              useCompactLabel={useCompactLabels}
              isActive={location.pathname.startsWith('/goals')}
            />

            {/* Pillar 2: Family & Friends */}
            <MobileNavItem
              icon={<Users className="h-5 w-5" />}
              label="Family"
              onClick={() => navigate('/assign')}
              useCompactLabel={useCompactLabels}
              isActive={location.pathname.startsWith('/assign') || location.pathname.startsWith('/connections')}
            />

            {/* Pillar 3: Explore UAE */}
            <MobileNavItem
              icon={<Map className="h-5 w-5" />}
              label="Explore"
              onClick={() => navigate('/explore')}
              useCompactLabel={useCompactLabels}
              isActive={location.pathname === '/explore'}
            />

            {/* Pillar 4: Passports */}
            <MobileNavItem
              icon={<Stamp className="h-5 w-5" />}
              label="Passports"
              onClick={() => navigate('/passports')}
              useCompactLabel={useCompactLabels}
              isActive={location.pathname.startsWith('/passports') || location.pathname.startsWith('/common-goals') || location.pathname.startsWith('/community-goals')}
            />


            {/* Tab 4: User Hub (logged in) or More Menu/Sign In (logged out) */}
            {user ? (
              <MobileUserHub 
                user={user} 
                onSignOut={handleSignOut}
                useCompactLabels={useCompactLabels}
              />
            ) : (
              <>
                {/* More Menu for guests to access Connections etc */}
                <MobileNavMoreMenu role={role} useCompactLabels={useCompactLabels} />
                
                {/* Sign In button */}
                <MobileNavItem 
                  icon={<User className="h-5 w-5" />}
                  label="Sign In"
                  onClick={handleSignIn}
                  useCompactLabel={useCompactLabels}
                  className="bg-primary/10 text-primary"
                />
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Auth Modal for sign-in/sign-up */}
      <AuthModal open={isAuthModalOpen} onOpenChange={setIsAuthModalOpen} />
    </>
  );
}
